import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import style from '../styles/CreateGroup.module.css';
import TagManagerModal from './TagManagerModal.tsx';
import { useAlertStore } from '../utils/alert';
import { useAuth } from '../contexts/AuthContext';

interface Tag {
  id: number;
  name: string;
}

export default function CreateGroup() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    groupName: '',
    description: '',
    privacy: 'public',
    tags: [] as Tag[],
  });

  const [validating, setValidating] = useState(false);
  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false);
  const { isAuthenticated, isLoading } = useAuth();

  /** Fetch user profile */
  const profileQuery = trpc.member.getMyProfile.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });

  /** Redirect unauthenticated users */
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isLoading, isAuthenticated, navigate]);

  /** Redirect if not a group member account */
  useEffect(() => {
    if (profileQuery.isFetched && !profileQuery.data) {
      navigate('/dashboard');
    }
  }, [profileQuery.isFetched, profileQuery.data, navigate]);

  /** Create group mutation */
  const createGroup = trpc.group.create.useMutation({
    onSuccess: (_data) => {
      useAlertStore.getState().showAlert({
        header: 'Success',
        body: 'Group created successfully',
        button: 'Ok',
      });
      navigate(`/dashboard`);
    },
    onError: (_error) => {
      useAlertStore.getState().showAlert({
        header: 'Error',
        body: 'Error creating group',
        button: 'Ok',
      });
    },
  });

  const handleFormData = (
    field: 'groupName' | 'description' | 'privacy',
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isValid = () => {
    return (
      formData.groupName.trim().length >= 3 &&
      formData.description.trim().length > 0 &&
      formData.tags.length >= 1 &&
      formData.tags.length <= 10
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidating(true);

    if (!isValid()) return;

    createGroup.mutate({
      name: formData.groupName.trim(),
      description: formData.description.trim(),
      privacy: formData.privacy as 'public' | 'private',
      tags: formData.tags.map((tag) => tag.name),
    });
  };

  return (
    <div className={style.container}>
      <div className={style.formContainer}>
        <h1>Create New Group</h1>

        <form onSubmit={handleSubmit} className={style.form}>
          {/* Group Name */}
          <div className={style.fieldContainer}>
            <label htmlFor="group-name-input">Group Name</label>
            <input
              id="group-name-input"
              type="text"
              value={formData.groupName}
              placeholder="e.g. Soup Lovers Unite!"
              onChange={(e) => handleFormData('groupName', e.target.value)}
              className={`${style.field} ${
                validating && formData.groupName.trim().length < 3 ? style.invalid : ''
              }`}
            />

            <p
              className={`${style.reminder} ${validating && formData.groupName.trim().length < 3 ? '' : style.hidden}`}
            >
              Group name must be at least 3 characters.
            </p>
          </div>

          {/* Description */}
          <div className={style.fieldContainer}>
            <label htmlFor="description-input">Description</label>
            <input
              id="description-input"
              type="text"
              value={formData.description}
              placeholder="A group for people who love soup!"
              onChange={(e) => handleFormData('description', e.target.value)}
              className={`${style.field} ${
                validating && !formData.description.trim() ? style.invalid : ''
              }`}
            />
            <p
              className={`${style.reminder} ${validating && !formData.description.trim() ? '' : style.hidden}`}
            >
              Description is required.
            </p>
          </div>

          {/* Privacy */}
          <div className={style.fieldContainer}>
            <label className={style.fieldLabel}>Privacy</label>
            <div className={style.switchRow}>
              <span className={formData.privacy === 'public' ? style.activeLabel : ''}>
                Public
              </span>

              <label className={style.switch}>
                <input
                  type="checkbox"
                  checked={formData.privacy === 'private'}
                  onChange={(e) =>
                    handleFormData('privacy', e.target.checked ? 'private' : 'public')
                  }
                />
                <span className={style.slider}></span>
              </label>

              <span className={formData.privacy === 'private' ? style.activeLabel : ''}>
                Private
              </span>
            </div>
          </div>

          {/* Tags Section */}
          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label>Interests</label>
              <button
                type="button"
                onClick={() => setIsTagSelectorOpen(true)}
                className={style.tagButton}
              >
                Choose Interests
              </button>
            </div>

            {/* Selected Preview */}
            <div className={style.tagPreview}>
              {formData.tags.map((tag) => (
                <span key={tag.id} className={style.tag}>
                  {tag.name}
                </span>
              ))}
            </div>

            <p
              className={`${style.reminder} ${validating && formData.tags.length === 0 ? '' : style.hidden}`}
            >
              Please select at least one interest.
            </p>
          </div>

          {/* Buttons */}
          <div className={style.buttonContainer}>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className={`${style.button} ${style.cancelButton}`}
            >
              Cancel
            </button>

            <span
              onMouseEnter={() => setValidating(true)}
              onMouseLeave={() => setValidating(false)}
            >
              <button
                type="submit"
                disabled={createGroup.isPending || !isValid()}
                className={`${style.button} ${style.submitButton}`}
              >
                {createGroup.isPending ? 'Creating...' : 'Create Group'}
              </button>
            </span>
          </div>
        </form>
      </div>

      {/* Tag Selector Modal */}
      {isTagSelectorOpen && (
        <TagManagerModal
          isOpen={isTagSelectorOpen}
          onClose={() => setIsTagSelectorOpen(false)}
          mode="select"
          min={1}
          max={10}
          onSave={(tags) => setFormData((prev) => ({ ...prev, tags }))}
          profileType="group"
          initialTags={formData.tags}
        />
      )}
    </div>
  );
}
